/**
 * Bond Mentor — Strategist Data (Drop 6)
 *
 * Sector rotation guidance, historical pattern matching,
 * global bond watch (Japan, Germany, UK, China), and carry trade risk.
 */

// ============================================
// SECTOR DEFINITIONS
// ============================================
export const SECTORS = {
  FINANCIALS: { name: 'Financials', ticker: 'XLF', icon: '🏦' },
  ENERGY: { name: 'Energy', ticker: 'XLE', icon: '⛽' },
  TECHNOLOGY: { name: 'Technology', ticker: 'XLK', icon: '💻' },
  HEALTHCARE: { name: 'Healthcare', ticker: 'XLV', icon: '🏥' },
  INDUSTRIALS: { name: 'Industrials', ticker: 'XLI', icon: '🏭' },
  CONSUMER_DISC: { name: 'Consumer Discretionary', ticker: 'XLY', icon: '🛍️' },
  CONSUMER_STAP: { name: 'Consumer Staples', ticker: 'XLP', icon: '🛒' },
  UTILITIES: { name: 'Utilities', ticker: 'XLU', icon: '⚡' },
  REAL_ESTATE: { name: 'Real Estate', ticker: 'XLRE', icon: '🏠' },
  MATERIALS: { name: 'Materials', ticker: 'XLB', icon: '⛏️' },
  COMM_SERVICES: { name: 'Comm. Services', ticker: 'XLC', icon: '📡' },
};

// ============================================
// STRATEGIST DATA PER SCENARIO
// ============================================
export const STRATEGIST_SCENARIOS = {
  ALL_CLEAR: {
    // Sector Rotation
    sectorRotation: {
      environment: 'Rising yields + normal curve + tight spreads',
      confidence: 78,
      confidenceLabel: 'High',
      confidenceColor: '#22c55e',
      favor: [
        { sector: 'FINANCIALS', reason: 'Banks profit from steeper yield curves and higher rates. Net interest margins expand.', strength: 'Strong' },
        { sector: 'ENERGY', reason: 'Rising yields often coincide with economic growth, boosting energy demand and pricing.', strength: 'Strong' },
        { sector: 'INDUSTRIALS', reason: 'Growth-sensitive sector benefits from healthy economic expansion and business investment.', strength: 'Moderate' },
        { sector: 'MATERIALS', reason: 'Commodity demand rises with economic growth. Inflation-linked revenue benefits.', strength: 'Moderate' },
      ],
      avoid: [
        { sector: 'UTILITIES', reason: 'High-dividend "bond proxy" sector hurt by rising yields. Investors sell utilities for actual bonds.', strength: 'Strong' },
        { sector: 'REAL_ESTATE', reason: 'Higher rates increase borrowing costs for property. REIT valuations compress.', strength: 'Strong' },
        { sector: 'CONSUMER_STAP', reason: 'Defensive sector underperforms when growth is strong. Money rotates into cyclicals.', strength: 'Moderate' },
      ],
    },

    // Historical Pattern Matching
    historicalPatterns: {
      bestMatch: {
        period: 'Mid-2017 (Post-Election Expansion)',
        matchScore: 82,
        matchColor: '#22c55e',
        conditions: 'Normal yield curve, tight spreads, moderate growth, Fed gradually tightening.',
        outcomes: {
          '3_month': { stocks: '+4.2%', label: 'Positive', color: '#22c55e' },
          '6_month': { stocks: '+8.1%', label: 'Positive', color: '#22c55e' },
          '12_month': { stocks: '+15.3%', label: 'Strong', color: '#22c55e' },
        },
        note: 'In 4 out of 5 historical matches with this setup, stocks delivered positive returns over the following year. The one exception was when an external shock (trade war) disrupted the pattern.',
      },
      secondMatch: {
        period: 'Mid-2014 (Goldilocks Economy)',
        matchScore: 68,
        matchColor: '#3b82f6',
        conditions: 'Normal curve, low volatility, steady growth, pre-tightening cycle.',
        outcomes: {
          '3_month': { stocks: '+2.8%', label: 'Positive', color: '#22c55e' },
          '6_month': { stocks: '+5.5%', label: 'Positive', color: '#22c55e' },
          '12_month': { stocks: '+11.2%', label: 'Positive', color: '#22c55e' },
        },
      },
    },

    // Global Bond Watch
    globalBonds: {
      divergenceLevel: 12,
      divergenceLabel: 'Low',
      divergenceColor: '#22c55e',
      divergenceNote: 'Major global bond markets are moving in similar directions. No cross-currents threatening U.S. markets.',
      countries: [
        { name: 'Japan', flag: '🇯🇵', yield10Y: 1.05, yieldTrend: 'STABLE', cbPosture: 'Gradual Tightening', cbColor: '#eab308', risk: 'LOW', riskColor: '#22c55e', note: 'BOJ slowly normalizing policy. No sudden moves expected. Carry trade risk minimal.' },
        { name: 'Germany', flag: '🇩🇪', yield10Y: 2.35, yieldTrend: 'STABLE', cbPosture: 'Holding', cbColor: '#3b82f6', risk: 'LOW', riskColor: '#22c55e', note: 'ECB on hold. European growth tepid but stable. Bunds trading orderly.' },
        { name: 'United Kingdom', flag: '🇬🇧', yield10Y: 4.20, yieldTrend: 'STABLE', cbPosture: 'Holding', cbColor: '#3b82f6', risk: 'LOW', riskColor: '#22c55e', note: 'BOE paused. UK fiscal situation stable. Gilts calm.' },
        { name: 'China', flag: '🇨🇳', yield10Y: 2.10, yieldTrend: 'DECLINING', cbPosture: 'Easing', cbColor: '#22c55e', risk: 'LOW', riskColor: '#22c55e', note: 'PBOC easing to support slowing economy. No spillover risk to U.S. currently.' },
      ],
      carryTrade: {
        risk: 'LOW',
        riskColor: '#22c55e',
        riskScore: 15,
        note: 'Yen carry trade is stable. Japanese yields rising gradually but not enough to trigger unwinding. The rate differential between Japan and the U.S. remains wide. No imminent carry trade risk.',
      },
    },

    analysis: 'The strategist view is favorable. Sector rotation favors cyclicals (Financials, Energy, Industrials) over defensives (Utilities, Real Estate). Historical pattern matching shows an 82% similarity to mid-2017, which saw +15.3% stock returns over the following year. Global bond markets are calm with no divergence threats. The yen carry trade is stable. This is an environment for growth-oriented positioning.',
  },

  EARLY_WARNING: {
    sectorRotation: {
      environment: 'Flattening curve + widening spreads + hawkish Fed',
      confidence: 55,
      confidenceLabel: 'Moderate',
      confidenceColor: '#eab308',
      favor: [
        { sector: 'HEALTHCARE', reason: 'Defensive growth. Less rate-sensitive than utilities. Steady earnings regardless of economy.', strength: 'Strong' },
        { sector: 'CONSUMER_STAP', reason: 'Defensive sector that outperforms in late-cycle environments. People buy groceries in any economy.', strength: 'Moderate' },
        { sector: 'ENERGY', reason: 'Inflation hedge. Energy prices often stay elevated even as growth slows, protecting margins.', strength: 'Moderate' },
      ],
      avoid: [
        { sector: 'TECHNOLOGY', reason: 'Long-duration sector hurt by rising real rates. High P/E multiples compress as rates stay elevated.', strength: 'Strong' },
        { sector: 'CONSUMER_DISC', reason: 'Consumer spending weakens as rates bite. Discretionary purchases are first to be cut.', strength: 'Strong' },
        { sector: 'REAL_ESTATE', reason: 'Double whammy: higher rates + slowing economy compress both valuations and rental demand.', strength: 'Strong' },
        { sector: 'FINANCIALS', reason: 'Flattening curve squeezes bank margins. Rising credit risk increases loan loss provisions.', strength: 'Moderate' },
      ],
    },

    historicalPatterns: {
      bestMatch: {
        period: 'Late 2018 (Fed Overtightening)',
        matchScore: 72,
        matchColor: '#eab308',
        conditions: 'Flat curve, hawkish Fed, widening spreads, trade tensions.',
        outcomes: {
          '3_month': { stocks: '-14.0%', label: 'Negative', color: '#ef4444' },
          '6_month': { stocks: '-5.2%', label: 'Negative', color: '#ef4444' },
          '12_month': { stocks: '+8.5%', label: 'Recovery', color: '#22c55e' },
        },
        note: 'In late 2018, similar conditions led to a sharp Q4 selloff before the Fed reversed course. Stocks recovered but only after the Fed pivoted to cutting. The lesson: the pain comes first, the recovery comes after the Fed admits its mistake.',
      },
      secondMatch: {
        period: 'Mid-2000 (Pre-Recession)',
        matchScore: 58,
        matchColor: '#f97316',
        conditions: 'Flat/inverting curve, overvalued stocks, Fed tightening.',
        outcomes: {
          '3_month': { stocks: '-8.5%', label: 'Negative', color: '#ef4444' },
          '6_month': { stocks: '-18.2%', label: 'Bear Market', color: '#ef4444' },
          '12_month': { stocks: '-25.0%', label: 'Severe', color: '#ef4444' },
        },
      },
    },

    globalBonds: {
      divergenceLevel: 45,
      divergenceLabel: 'Moderate',
      divergenceColor: '#eab308',
      divergenceNote: 'Divergence building. U.S. yields rising while Asian yields declining. Cross-currents developing that could create volatility.',
      countries: [
        { name: 'Japan', flag: '🇯🇵', yield10Y: 1.25, yieldTrend: 'RISING', cbPosture: 'Tightening', cbColor: '#f97316', risk: 'MODERATE', riskColor: '#eab308', note: 'BOJ raising rates faster than expected. Yen strengthening. Carry trade under pressure.' },
        { name: 'Germany', flag: '🇩🇪', yield10Y: 2.55, yieldTrend: 'RISING', cbPosture: 'Hawkish Hold', cbColor: '#f97316', risk: 'LOW', riskColor: '#22c55e', note: 'ECB signaling higher-for-longer. European growth stalling but inflation sticky.' },
        { name: 'United Kingdom', flag: '🇬🇧', yield10Y: 4.65, yieldTrend: 'RISING', cbPosture: 'Hawkish', cbColor: '#f97316', risk: 'MODERATE', riskColor: '#eab308', note: 'Gilt yields rising on fiscal concerns. UK budget deficits expanding. Mini-2022 echoes.' },
        { name: 'China', flag: '🇨🇳', yield10Y: 1.85, yieldTrend: 'DECLINING', cbPosture: 'Aggressive Easing', cbColor: '#22c55e', risk: 'MODERATE', riskColor: '#eab308', note: 'PBOC cutting aggressively. Capital outflows accelerating. Yuan weakening, which pressures U.S. trade dynamics.' },
      ],
      carryTrade: {
        risk: 'MODERATE',
        riskColor: '#eab308',
        riskScore: 48,
        note: 'Carry trade stress building. Japanese yields have jumped to 1.25% from below 1%. The rate differential is narrowing. If BOJ tightens further, carry trade unwinding could accelerate, causing forced selling of U.S. assets.',
      },
    },

    analysis: 'The strategist view calls for defensive rotation. Shift from cyclicals to defensive sectors (Healthcare, Staples). Historical pattern matching shows 72% similarity to late 2018, which saw a -14% drawdown before recovery. Global divergence is building with Japan tightening while China eases — cross-currents that create volatility. Carry trade risk is moderate and rising. This is an environment to reduce risk, favor quality, and stay nimble.',
  },

  DANGER_ZONE: {
    sectorRotation: {
      environment: 'Inverted curve + blown spreads + vigilante crisis + liquidity failure',
      confidence: 85,
      confidenceLabel: 'High (Defensive)',
      confidenceColor: '#ef4444',
      favor: [
        { sector: 'CONSUMER_STAP', reason: 'Ultimate defensive sector. People buy food and toilet paper in recessions. Stable earnings.', strength: 'Strong' },
        { sector: 'HEALTHCARE', reason: 'Non-discretionary spending. People still go to the doctor. Recession-resistant revenue.', strength: 'Strong' },
        { sector: 'UTILITIES', reason: 'Yields will plummet when the Fed is forced to cut. Utility "bond proxy" status becomes an asset again.', strength: 'Moderate' },
      ],
      avoid: [
        { sector: 'FINANCIALS', reason: 'Banks face loan losses, margin compression, and potential capital stress. Worst sector in recessions.', strength: 'Strong' },
        { sector: 'CONSUMER_DISC', reason: 'Consumer spending collapses. Retail, autos, restaurants — all face severe headwinds.', strength: 'Strong' },
        { sector: 'INDUSTRIALS', reason: 'Business investment freezes. Order books dry up. Layoffs begin.', strength: 'Strong' },
        { sector: 'TECHNOLOGY', reason: 'Ad spending cut, enterprise budgets frozen, multiple compression accelerates.', strength: 'Strong' },
        { sector: 'REAL_ESTATE', reason: 'Commercial real estate faces vacancies, refinancing crises, and potential defaults.', strength: 'Strong' },
        { sector: 'MATERIALS', reason: 'Commodity demand collapses with economic activity. Cyclical wipeout.', strength: 'Strong' },
      ],
    },

    historicalPatterns: {
      bestMatch: {
        period: 'Q3 2007 (Pre-Great Financial Crisis)',
        matchScore: 78,
        matchColor: '#ef4444',
        conditions: 'Inverted curve steepening, credit blowout, liquidity crisis emerging, Fed behind.',
        outcomes: {
          '3_month': { stocks: '-10.5%', label: 'Negative', color: '#ef4444' },
          '6_month': { stocks: '-28.0%', label: 'Bear Market', color: '#ef4444' },
          '12_month': { stocks: '-42.0%', label: 'Crash', color: '#ef4444' },
        },
        note: 'The current environment shares alarming similarities with Q3 2007: inverted curve, credit spreads blowing out, liquidity drying up, Fed insisting the economy is fine. In 2007, stocks lost 42% over the next year. Past is not guaranteed to repeat, but this pattern demands extreme caution.',
      },
      secondMatch: {
        period: 'Feb 2020 (Pre-COVID Crash)',
        matchScore: 62,
        matchColor: '#f97316',
        conditions: 'Curve had inverted in 2019, credit tightening, liquidity stress building.',
        outcomes: {
          '3_month': { stocks: '-34.0%', label: 'Crash', color: '#ef4444' },
          '6_month': { stocks: '-8.0%', label: 'Partial Recovery', color: '#eab308' },
          '12_month': { stocks: '+16.0%', label: 'Full Recovery', color: '#22c55e' },
        },
      },
    },

    globalBonds: {
      divergenceLevel: 75,
      divergenceLabel: 'Severe',
      divergenceColor: '#ef4444',
      divergenceNote: 'MAJOR GLOBAL DIVERGENCE. U.S. in crisis while some global markets still function. Capital flow dislocations creating unpredictable cross-border effects.',
      countries: [
        { name: 'Japan', flag: '🇯🇵', yield10Y: 1.55, yieldTrend: 'SPIKING', cbPosture: 'Emergency Tightening', cbColor: '#ef4444', risk: 'EXTREME', riskColor: '#ef4444', note: 'BOJ forced to tighten aggressively to defend yen. Japanese yields spiking. CARRY TRADE UNWINDING. Forced selling of U.S. Treasuries and stocks by Japanese institutions.' },
        { name: 'Germany', flag: '🇩🇪', yield10Y: 2.85, yieldTrend: 'VOLATILE', cbPosture: 'Crisis Mode', cbColor: '#ef4444', risk: 'HIGH', riskColor: '#f97316', note: 'European banks under stress. ECB caught between inflation and financial stability. Bund market volatile.' },
        { name: 'United Kingdom', flag: '🇬🇧', yield10Y: 5.10, yieldTrend: 'SPIKING', cbPosture: 'Emergency Response', cbColor: '#ef4444', risk: 'HIGH', riskColor: '#f97316', note: 'Gilt crisis underway. UK pension funds under margin pressure. BOE forced to intervene. Echoes of September 2022 LDI crisis.' },
        { name: 'China', flag: '🇨🇳', yield10Y: 1.55, yieldTrend: 'DECLINING', cbPosture: 'Full Easing', cbColor: '#22c55e', risk: 'HIGH', riskColor: '#f97316', note: 'Chinese economy in sharp slowdown. Capital flight accelerating. Yuan under pressure. Potential forced selling of U.S. Treasuries to defend currency.' },
      ],
      carryTrade: {
        risk: 'EXTREME',
        riskColor: '#ef4444',
        riskScore: 88,
        note: 'CARRY TRADE CRISIS. Japanese yields have spiked to 1.55%, narrowing the rate differential dramatically. Japanese institutional investors are being forced to sell U.S. assets and repatriate capital. This unwinding is adding massive selling pressure on U.S. Treasuries and stocks simultaneously. The July 2024 carry trade scare was a preview — this is the full event.',
      },
    },

    analysis: 'The strategist view is maximum defensive. Sector rotation strongly favors only Consumer Staples and Healthcare — everything else is in the danger zone. Historical pattern matching shows 78% similarity to Q3 2007, which preceded a -42% stock market crash. Global bonds are in severe divergence with Japan\'s carry trade unwinding, UK gilts in crisis, and China\'s capital flight pressuring U.S. assets. This is not an environment for stock picking — it is an environment for capital preservation. The only question is how much cash and short-term Treasuries to hold.',
  },
};
